import { useEffect, useRef, useState } from "react";

const StrategicDesign2Clean = () => {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.15 }
    );

    document.querySelectorAll("[data-animate]").forEach((el) => {
      observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const isVisible = (id) => visibleSections.has(id);

  const values = [
    {
      name: "Respect",
      desc: "We listen to diverse perspectives and value the dignity of every learner and colleague.",
      icon: "🤝",
    },
    {
      name: "Integrity",
      desc: "We act ethically, transparently, and responsibly in teaching, governance, and institutional practice.",
      icon: "⚖️",
    },
    {
      name: "Innovation",
      desc: "We embrace creative and digitally enabled approaches that improve learning and institutional effectiveness.",
      icon: "💡",
    },
    {
      name: "Excellence",
      desc: "We pursue high standards in academic delivery, student experience, governance, and operations.",
      icon: "🏆",
    },
    {
      name: "Collaboration",
      desc: "We believe strong institutions and strong learners are built through partnership, teamwork, and shared purpose.",
      icon: "🌐",
    },
    {
      name: "Impact",
      desc: "We aim to produce outcomes that matter in real professions, real communities, and real public life.",
      icon: "🎯",
    },
  ];

  const priorities = [
    {
      num: "01",
      title: "Build Our Institutional Foundation",
      desc: "Launch our first accredited programme for March 2027, prepare for September 2027, and ensure governance, staffing, digital systems, and controls are fully operational.",
      progress: 75,
    },
    {
      num: "02",
      title: "Deliver High-Quality Teaching and Learning",
      desc: "Provide academically strong, accessible, and engaging online teaching supported by well-prepared lecturers, sound assessment, and learner-centred design.",
      progress: 65,
    },
    {
      num: "03",
      title: "Strengthen Student Recruitment, Admissions and Support",
      desc: "Build a sustainable student pipeline through focused recruitment, efficient admissions, structured onboarding, and responsive student support.",
      progress: 55,
    },
    {
      num: "04",
      title: "Grow Our Academic and Professional Learning Portfolio",
      desc: "Strengthen the MA delivery, expand certified and executive learning opportunities, and prepare for future academic development.",
      progress: 45,
    },
    {
      num: "05",
      title: "Embed Quality Assurance and Good Governance",
      desc: "Grow on a foundation of discipline, evidence, accountability, and good governance for institutional learning and improvement.",
      progress: 60,
    },
    {
      num: "06",
      title: "Build Partnerships, Public Engagement and External Reach",
      desc: "Strengthen visibility, stakeholder engagement, labour market relevance, and public-facing educational contribution.",
      progress: 40,
    },
    {
      num: "07",
      title: "Secure Sustainability and Institutional Maturity",
      desc: "Build a financially sustainable, risk-aware institution that grows carefully and makes proportionate decisions.",
      progress: 50,
    },
  ];

  const timeline = [
    {
      year: "2025",
      label: "Foundation",
      desc: "Governance, policy, accreditation preparation",
      status: "complete",
    },
    {
      year: "2026",
      label: "Readiness",
      desc: "Systems, staffing, programme finalisation",
      status: "active",
    },
    {
      year: "Mar 2027",
      label: "First Intake",
      desc: "MA in Global Business & Strategic Leadership launches",
      status: "upcoming",
    },
    {
      year: "Sep 2027",
      label: "Second Intake",
      desc: "Consolidation and second cohort activation",
      status: "upcoming",
    },
    {
      year: "2028-29",
      label: "Growth",
      desc: "Controlled expansion, portfolio development",
      status: "future",
    },
    {
      year: "2030",
      label: "Maturity",
      desc: "Institutional sustainability and recognition",
      status: "future",
    },
  ];

  const enablers = [
    { label: "Digital-First Delivery", pct: 90 },
    { label: "Governance & QA", pct: 85 },
    { label: "Mission-Led Planning", pct: 80 },
    { label: "Evidence Systems", pct: 75 },
    { label: "Partnership Building", pct: 60 },
    { label: "Financial Planning", pct: 70 },
    { label: "Values-Based Leadership", pct: 95 },
  ];

  return (
    <div
      style={{
        fontFamily: "var(--th-sans, 'Outfit', sans-serif)",
        color: "#1A1A2E",
        background: "#F8F7F4",
        overflowX: "hidden",
      }}
    >
      <style>{`
        .sa-hero {
          min-height: min(100vh, 820px);
          background: linear-gradient(160deg, #081428 0%, #0C1F3F 42%, #142d54 100%);
          position: relative;
          display: flex;
          align-items: center;
          overflow: hidden;
        }
        .sa-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(197,164,78,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(197,164,78,0.03) 1px, transparent 1px);
          background-size: 60px 60px;
        }
        .sa-hero-ring {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(197,164,78,0.1);
          animation: ringPulse 6s ease-in-out infinite;
        }
        @keyframes ringPulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.03); }
        }
        .sa-hero-content {
          position: relative;
          z-index: 2;
          max-width: 900px;
          margin: 0 auto;
          padding: 120px 48px 80px;
        }
        .sa-badge {
          display: inline-block;
          border: 1px solid rgba(197,164,78,0.45);
          padding: 6px 20px;
          border-radius: 40px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: #C5A44E;
          margin-bottom: 32px;
          animation: fadeSlideUp 0.8s ease both;
        }
        .sa-hero-title {
          font-family: var(--th-serif, 'Cormorant Garamond', serif);
          font-size: clamp(42px, 7vw, 72px);
          font-weight: 700;
          color: #fff;
          line-height: 1.08;
          margin-bottom: 12px;
          animation: fadeSlideUp 0.8s ease 0.2s both;
        }
        .sa-hero-title .gold { color: #D4B96A; }
        .sa-hero-sub {
          font-size: 18px;
          font-weight: 300;
          color: rgba(255,255,255,0.58);
          letter-spacing: 8px;
          margin-bottom: 36px;
          animation: fadeSlideUp 0.8s ease 0.4s both;
        }
        .sa-hero-desc {
          font-size: 17px;
          line-height: 1.7;
          color: rgba(255,255,255,0.72);
          max-width: 600px;
          animation: fadeSlideUp 0.8s ease 0.6s both;
        }
        .sa-gold-bar {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #C5A44E, #D4B96A, #C5A44E);
        }
        .sa-scroll-indicator {
          position: absolute;
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          animation: bounce 2s ease-in-out infinite;
          z-index: 2;
        }
        .sa-scroll-indicator span {
          font-size: 10px;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: rgba(197,164,78,0.5);
        }
        .sa-scroll-line {
          width: 1px;
          height: 40px;
          background: linear-gradient(180deg, #C5A44E, transparent);
        }
        @keyframes bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(10px); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .sa-section {
          padding: 100px 48px;
          max-width: 1100px;
          margin: 0 auto;
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .sa-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        .sa-section-label {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 4px;
          text-transform: uppercase;
          color: #C5A44E;
          margin-bottom: 12px;
        }
        .sa-section-title {
          font-family: var(--th-serif, 'Cormorant Garamond', serif);
          font-size: clamp(28px, 4vw, 42px);
          font-weight: 700;
          color: #0C1F3F;
          margin-bottom: 8px;
          line-height: 1.15;
        }
        .sa-gold-rule {
          width: 60px;
          height: 3px;
          background: linear-gradient(90deg, #C5A44E, #D4B96A);
          margin: 20px 0 32px;
          border-radius: 2px;
        }
        .sa-quote-block {
          background: linear-gradient(135deg, #0C1F3F 0%, #142d54 100%);
          border-radius: 16px;
          padding: 56px 48px;
          margin: 80px auto;
          max-width: 1100px;
          position: relative;
          overflow: hidden;
        }
        .sa-quote-block::before {
          content: '“';
          font-family: var(--th-serif, 'Cormorant Garamond', serif);
          font-size: 160px;
          color: rgba(197,164,78,0.08);
          position: absolute;
          top: -20px;
          left: 24px;
          line-height: 1;
        }
        .sa-quote-text {
          font-family: var(--th-serif, 'Cormorant Garamond', serif);
          font-size: 22px;
          line-height: 1.7;
          color: rgba(255,255,255,0.87);
          position: relative;
          z-index: 1;
          margin-bottom: 24px;
          font-style: italic;
        }
        .sa-quote-author {
          color: #D4B96A;
          font-weight: 600;
          font-size: 14px;
          letter-spacing: 1px;
        }
        .sa-quote-role {
          color: rgba(255,255,255,0.5);
          font-size: 13px;
          margin-top: 4px;
        }
        .sa-vm-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          margin-top: 20px;
        }
        @media (max-width: 768px) { .sa-vm-grid { grid-template-columns: 1fr; } }
        .sa-vm-card {
          background: #fff;
          border-radius: 12px;
          padding: 40px 36px;
          border: 1px solid rgba(12,31,63,0.08);
          box-shadow: 0 4px 24px rgba(0,0,0,0.04);
          position: relative;
          overflow: hidden;
        }
        .sa-vm-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #C5A44E, #D4B96A);
        }
        .sa-vm-card h3 {
          font-family: var(--th-serif, 'Cormorant Garamond', serif);
          font-size: 24px;
          color: #0C1F3F;
          margin-bottom: 12px;
        }
        .sa-vm-card p {
          font-size: 15px;
          line-height: 1.7;
          color: #555570;
        }
        .sa-values-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-top: 32px;
        }
        @media (max-width: 900px) { .sa-values-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .sa-values-grid { grid-template-columns: 1fr; } }
        .sa-value-card {
          background: #fff;
          border-radius: 12px;
          padding: 32px 28px;
          border: 1px solid rgba(12,31,63,0.06);
          box-shadow: 0 4px 20px rgba(0,0,0,0.03);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          cursor: default;
        }
        .sa-value-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(12,31,63,0.08);
        }
        .sa-value-icon {
          font-size: 32px;
          margin-bottom: 16px;
          display: block;
        }
        .sa-value-card h4 {
          font-family: var(--th-serif, 'Cormorant Garamond', serif);
          font-size: 22px;
          color: #0C1F3F;
          margin-bottom: 8px;
        }
        .sa-value-card p {
          font-size: 14px;
          line-height: 1.6;
          color: #555570;
        }
        .sa-priorities-list {
          display: flex;
          flex-direction: column;
          gap: 24px;
          margin-top: 32px;
        }
        .sa-priority {
          background: #fff;
          border-radius: 12px;
          padding: 32px 36px;
          border: 1px solid rgba(12,31,63,0.06);
          box-shadow: 0 2px 16px rgba(0,0,0,0.03);
          display: flex;
          gap: 28px;
          align-items: flex-start;
          transition: transform 0.3s ease;
        }
        .sa-priority:hover { transform: translateX(4px); }
        .sa-priority-num {
          font-family: var(--th-serif, 'Cormorant Garamond', serif);
          font-size: 36px;
          font-weight: 700;
          color: rgba(197,164,78,0.22);
          line-height: 1;
          flex-shrink: 0;
          width: 56px;
        }
        .sa-priority-content { flex: 1; }
        .sa-priority h4 {
          font-family: var(--th-serif, 'Cormorant Garamond', serif);
          font-size: 24px;
          color: #0C1F3F;
          margin-bottom: 8px;
        }
        .sa-priority p {
          font-size: 14px;
          line-height: 1.6;
          color: #555570;
          margin-bottom: 14px;
        }
        .sa-progress-track {
          height: 4px;
          background: rgba(12,31,63,0.08);
          border-radius: 2px;
          overflow: hidden;
        }
        .sa-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #C5A44E, #D4B96A);
          border-radius: 2px;
          transition: width 1.5s ease;
        }
        .sa-timeline-section {
          background: linear-gradient(160deg, #081428 0%, #0C1F3F 60%, #142d54 100%);
          padding: 100px 48px;
          position: relative;
          overflow: hidden;
        }
        .sa-timeline-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(197,164,78,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(197,164,78,0.02) 1px, transparent 1px);
          background-size: 60px 60px;
        }
        .sa-timeline-inner {
          max-width: 1100px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }
        .sa-timeline-track {
          display: flex;
          gap: 0;
          margin-top: 48px;
          position: relative;
        }
        .sa-timeline-track::before {
          content: '';
          position: absolute;
          top: 28px;
          left: 0;
          right: 0;
          height: 2px;
          background: rgba(197,164,78,0.15);
        }
        .sa-timeline-item {
          flex: 1;
          text-align: center;
          position: relative;
          padding-top: 56px;
        }
        .sa-timeline-dot {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          position: absolute;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 2;
        }
        .sa-timeline-dot.complete { background: #C5A44E; box-shadow: 0 0 12px rgba(197,164,78,0.4); }
        .sa-timeline-dot.active { background: #D4B96A; box-shadow: 0 0 20px rgba(212,185,106,0.6); animation: dotPulse 2s ease-in-out infinite; }
        .sa-timeline-dot.upcoming { background: rgba(197,164,78,0.4); border: 2px solid #C5A44E; }
        .sa-timeline-dot.future { background: rgba(197,164,78,0.15); border: 1px solid rgba(197,164,78,0.3); }
        @keyframes dotPulse {
          0%, 100% { box-shadow: 0 0 12px rgba(212,185,106,0.4); }
          50% { box-shadow: 0 0 24px rgba(212,185,106,0.8); }
        }
        .sa-timeline-year {
          font-family: var(--th-serif, 'Cormorant Garamond', serif);
          font-size: 18px;
          font-weight: 700;
          color: #D4B96A;
          margin-bottom: 4px;
        }
        .sa-timeline-label {
          font-size: 13px;
          font-weight: 600;
          color: rgba(255,255,255,0.8);
          margin-bottom: 4px;
        }
        .sa-timeline-desc {
          font-size: 11px;
          color: rgba(255,255,255,0.45);
          line-height: 1.5;
          padding: 0 8px;
        }
        .sa-enablers-grid {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 32px;
        }
        .sa-enabler-row {
          display: flex;
          align-items: center;
          gap: 20px;
        }
        .sa-enabler-label {
          font-size: 14px;
          font-weight: 500;
          color: #1A1A2E;
          width: 200px;
          flex-shrink: 0;
          text-align: right;
        }
        .sa-enabler-bar-track {
          flex: 1;
          height: 28px;
          background: rgba(12,31,63,0.06);
          border-radius: 6px;
          overflow: hidden;
          position: relative;
        }
        .sa-enabler-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #0C1F3F, #142d54, #C5A44E);
          border-radius: 6px;
          transition: width 1.8s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding-right: 10px;
        }
        .sa-enabler-pct {
          font-size: 11px;
          font-weight: 700;
          color: #fff;
          letter-spacing: 0.5px;
        }
        .sa-cta-section {
          background: linear-gradient(160deg, #0C1F3F 0%, #142d54 100%);
          padding: 100px 48px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .sa-cta-section::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 500px;
          height: 500px;
          border-radius: 50%;
          border: 1px solid rgba(197,164,78,0.06);
        }
        .sa-cta-title {
          font-family: var(--th-serif, 'Cormorant Garamond', serif);
          font-size: clamp(32px, 4vw, 44px);
          font-weight: 700;
          color: #fff;
          margin-bottom: 20px;
          position: relative;
          z-index: 1;
        }
        .sa-cta-desc {
          font-size: 17px;
          line-height: 1.7;
          color: rgba(255,255,255,0.62);
          max-width: 640px;
          margin: 0 auto 40px;
          position: relative;
          z-index: 1;
        }
        .sa-cta-links {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
          position: relative;
          z-index: 1;
        }
        .sa-cta-btn {
          display: inline-block;
          padding: 14px 36px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 1px;
          text-decoration: none;
          transition: all 0.3s ease;
          cursor: pointer;
          border: none;
        }
        .sa-cta-btn.primary {
          background: linear-gradient(135deg, #C5A44E, #D4B96A);
          color: #081428;
        }
        .sa-cta-btn.primary:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(197,164,78,0.3); }
        .sa-cta-btn.secondary {
          background: transparent;
          color: #C5A44E;
          border: 1px solid rgba(197,164,78,0.4);
        }
        .sa-cta-btn.secondary:hover { background: rgba(197,164,78,0.08); }
        @media (max-width: 768px) {
          .sa-section { padding: 60px 24px; }
          .sa-timeline-track { flex-direction: column; gap: 24px; }
          .sa-timeline-track::before { display: none; }
          .sa-timeline-item { text-align: left; padding-top: 0; padding-left: 40px; }
          .sa-timeline-dot { left: 0; top: 4px; transform: none; }
          .sa-enabler-row { flex-direction: column; align-items: stretch; gap: 6px; }
          .sa-enabler-label { text-align: left; width: auto; }
          .sa-priority { flex-direction: column; gap: 12px; }
          .sa-hero-content { padding: 100px 24px 80px; }
        }
      `}</style>

      <section className="sa-hero">
        <div className="sa-gold-bar" />
        <div
          className="sa-hero-ring"
          style={{ width: 500, height: 500, top: -100, right: -100 }}
        />
        <div
          className="sa-hero-ring"
          style={{
            width: 350,
            height: 350,
            top: -25,
            right: -25,
            animationDelay: "2s",
          }}
        />
        <div
          className="sa-hero-ring"
          style={{
            width: 200,
            height: 200,
            bottom: 100,
            left: -50,
            animationDelay: "4s",
          }}
        />
        <div className="sa-hero-content">
          <div className="sa-badge">ESBD · Malta · 2025-2030</div>
          <h1 className="sa-hero-title">
            Strategic
            <br />
            <span className="gold">Ambition</span>
          </h1>
          <div className="sa-hero-sub">2 0 2 5 — 2 0 3 0</div>
          <p className="sa-hero-desc">
            Our compass for shaping the future of accessible, high-quality
            online higher education in leadership, diplomacy, business, and
            governance.
          </p>
        </div>
        <div className="sa-scroll-indicator">
          <span>Explore</span>
          <div className="sa-scroll-line" />
        </div>
      </section>

      <div className="sa-quote-block" style={{ margin: "0 auto", borderRadius: 0 }}>
        <p className="sa-quote-text">
          At the European School of Business & Diplomacy, strategy is more than
          a document. It is our compass for shaping the future. We are building
          carefully. Our first task is to launch well, govern well, and teach
          well.
        </p>
        <div className="sa-quote-author">Dr Emmanuel Addo</div>
        <div className="sa-quote-role">President and Founder, ESBD</div>
      </div>

      <div
        id="malta"
        data-animate
        className={`sa-section ${isVisible("malta") ? "visible" : ""}`}
        style={{ maxWidth: 1100, margin: "0 auto" }}
      >
        <div style={{ display: "flex", gap: 56, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 480px" }}>
            <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
              <span style={{ background: "#0C1F3F", color: "#fff", padding: "6px 18px", borderRadius: 40, fontSize: 12, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase" }}>
                Strategic Ambition
              </span>
              <span style={{ border: "1px solid rgba(12,31,63,0.2)", color: "#0C1F3F", padding: "6px 18px", borderRadius: 40, fontSize: 12, fontWeight: 500, letterSpacing: 2 }}>
                2025 — 2030
              </span>
            </div>
            <h2 style={{ fontFamily: "var(--th-serif, 'Cormorant Garamond', serif)", fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 700, color: "#0C1F3F", lineHeight: 1.15, marginBottom: 8 }}>
              From Malta to the World -{" "}
              <em style={{ color: "#C5A44E", fontStyle: "italic" }}>
                Building the Leaders of Tomorrow, Today
              </em>
            </h2>
            <p style={{ fontFamily: "var(--th-serif, 'Cormorant Garamond', serif)", fontSize: 18, fontStyle: "italic", color: "#555570", marginBottom: 24 }}>
              Strategy is our compass for shaping the future.
            </p>
            <p style={{ fontSize: 15, lineHeight: 1.75, color: "#2D2D44", marginBottom: 16 }}>
              At the European School of Business &amp; Diplomacy, strategy is
              more than a document. This Strategic Ambition 2025 to 2030
              reflects our clear commitment to building a new kind of
              institution, one rooted in integrity, driven by innovation, and
              focused on preparing leaders to tackle global challenges.
            </p>
            <p style={{ fontSize: 15, lineHeight: 1.75, color: "#2D2D44" }}>
              We are a new institution, but we are intentional in everything we
              do. From Malta, we are building a digitally connected and globally
              engaged academic community that puts learners at the centre.
            </p>
          </div>
          <div style={{ flex: "0 0 340px", display: "flex", justifyContent: "center" }}>
            <div style={{ background: "#F8F5EE", borderRadius: 16, padding: 32, width: 320, height: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg
                viewBox="0 0 280 260"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: "100%", height: "100%" }}
              >
                <ellipse cx="140" cy="120" rx="65" ry="60" fill="#1A6B5A" opacity="0.85" />
                <ellipse cx="140" cy="120" rx="65" ry="60" fill="none" stroke="#14594A" strokeWidth="1.5" />
                <path d="M95 100 Q120 80 140 100 Q160 120 185 100" stroke="#1DA88A" strokeWidth="1.5" fill="none" opacity="0.5" />
                <path d="M85 130 Q120 110 140 130 Q160 150 195 130" stroke="#1DA88A" strokeWidth="1.5" fill="none" opacity="0.4" />
                <line x1="140" y1="60" x2="140" y2="180" stroke="#1DA88A" strokeWidth="1" opacity="0.3" />
                <ellipse cx="140" cy="120" rx="30" ry="60" fill="none" stroke="#1DA88A" strokeWidth="1" opacity="0.3" />
                <circle cx="60" cy="185" r="10" fill="#E8915A" />
                <path d="M60 195 L60 230 M60 210 L45 200 M60 210 L80 105" stroke="#E8915A" strokeWidth="3.5" strokeLinecap="round" />
                <rect x="52" y="230" width="16" height="22" rx="3" fill="#D4543A" />
                <circle cx="100" cy="175" r="9" fill="#2B5EA7" />
                <path d="M100 184 L100 225 M100 200 L85 190 M100 200 L120 110" stroke="#2B5EA7" strokeWidth="3" strokeLinecap="round" />
                <rect x="93" y="225" width="14" height="20" rx="3" fill="#1A4680" />
                <circle cx="175" cy="170" r="11" fill="#C5A44E" />
                <path d="M175 181 L175 228 M175 198 L195 185 M175 198 L158 108" stroke="#C5A44E" strokeWidth="3.5" strokeLinecap="round" />
                <rect x="167" y="228" width="16" height="22" rx="3" fill="#A88A3A" />
                <circle cx="220" cy="188" r="9" fill="#D4543A" />
                <path d="M220 197 L220 235 M220 212 L235 202 M220 212 L195 115" stroke="#D4543A" strokeWidth="3" strokeLinecap="round" />
                <rect x="213" y="235" width="14" height="18" rx="3" fill="#B83A2A" />
                <circle cx="138" cy="200" r="7" fill="#E8915A" />
                <path d="M138 207 L138 240 M138 220 L128 212 M138 220 L148 212" stroke="#E8915A" strokeWidth="2.5" strokeLinecap="round" />
                <rect x="132" y="240" width="12" height="14" rx="2" fill="#D07040" />
                <circle cx="50" cy="160" r="3" fill="#C5A44E" opacity="0.3" />
                <circle cx="240" cy="165" r="2.5" fill="#C5A44E" opacity="0.25" />
                <circle cx="140" cy="50" r="2" fill="#1DA88A" opacity="0.3" />
              </svg>
            </div>
          </div>
        </div>
        <div style={{ height: 1, background: "linear-gradient(90deg, transparent, rgba(12,31,63,0.08), transparent)", margin: "60px 0 0" }} />
      </div>

      <div id="vm" data-animate className={`sa-section ${isVisible("vm") ? "visible" : ""}`}>
        <div className="sa-section-label">Who We Are</div>
        <div className="sa-section-title">Vision & Mission</div>
        <div className="sa-gold-rule" />
        <div className="sa-vm-grid">
          <div className="sa-vm-card">
            <h3>Our Vision</h3>
            <p>
              To be a forward-thinking higher education institution recognised
              for accessible online teaching and learning, developing ethical,
              globally minded leaders who create positive impact in their
              professions, communities, and wider society.
            </p>
          </div>
          <div className="sa-vm-card">
            <h3>Our Mission</h3>
            <p>
              To deliver accessible, high-quality online teaching and learning
              in leadership, diplomacy, business, and governance, equipping
              diverse learners for responsible professional practice and
              contributing to society through real-world, public-facing
              education and engagement.
            </p>
          </div>
        </div>
      </div>

      <div id="values" data-animate className={`sa-section ${isVisible("values") ? "visible" : ""}`}>
        <div className="sa-section-label">Our Foundation</div>
        <div className="sa-section-title">Core Values</div>
        <div className="sa-gold-rule" />
        <div className="sa-values-grid">
          {values.map((v, i) => (
            <div key={v.name} className="sa-value-card" style={{ transitionDelay: `${i * 0.1}s` }}>
              <span className="sa-value-icon">{v.icon}</span>
              <h4>{v.name}</h4>
              <p>{v.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div id="priorities" data-animate className={`sa-section ${isVisible("priorities") ? "visible" : ""}`}>
        <div className="sa-section-label">Our Direction</div>
        <div className="sa-section-title">Strategic Priorities</div>
        <div className="sa-gold-rule" />
        <div className="sa-priorities-list">
          {priorities.map((p) => (
            <div key={p.num} className="sa-priority">
              <div className="sa-priority-num">{p.num}</div>
              <div className="sa-priority-content">
                <h4>{p.title}</h4>
                <p>{p.desc}</p>
                <div className="sa-progress-track">
                  <div className="sa-progress-fill" style={{ width: isVisible("priorities") ? `${p.progress}%` : "0%" }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <section className="sa-timeline-section">
        <div
          id="timeline"
          data-animate
          className={`sa-timeline-inner ${isVisible("timeline") ? "visible" : ""}`}
          style={{ opacity: isVisible("timeline") ? 1 : 0, transform: isVisible("timeline") ? "translateY(0)" : "translateY(40px)", transition: "all 0.8s ease" }}
        >
          <div className="sa-section-label" style={{ color: "#C5A44E" }}>Our Roadmap</div>
          <div className="sa-section-title" style={{ color: "#fff" }}>Direction of Travel</div>
          <div className="sa-gold-rule" />
          <div className="sa-timeline-track">
            {timeline.map((t) => (
              <div key={t.year} className="sa-timeline-item">
                <div className={`sa-timeline-dot ${t.status}`} />
                <div className="sa-timeline-year">{t.year}</div>
                <div className="sa-timeline-label">{t.label}</div>
                <div className="sa-timeline-desc">{t.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div id="enablers" data-animate className={`sa-section ${isVisible("enablers") ? "visible" : ""}`}>
        <div className="sa-section-label">What Powers Us</div>
        <div className="sa-section-title">Strategic Enablers</div>
        <div className="sa-gold-rule" />
        <div className="sa-enablers-grid">
          {enablers.map((e, i) => (
            <div key={e.label} className="sa-enabler-row" style={{ transitionDelay: `${i * 0.1}s` }}>
              <div className="sa-enabler-label">{e.label}</div>
              <div className="sa-enabler-bar-track">
                <div className="sa-enabler-bar-fill" style={{ width: isVisible("enablers") ? `${e.pct}%` : "0%" }}>
                  <span className="sa-enabler-pct">{e.pct}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div id="strengths" data-animate className={`sa-section ${isVisible("strengths") ? "visible" : ""}`}>
        <div className="sa-section-label">What Sets Us Apart</div>
        <div className="sa-section-title">Our Institutional Strengths</div>
        <div className="sa-gold-rule" />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginTop: 28 }}>
          {[
            { icon: "🎓", text: "A distinctive academic focus across leadership, diplomacy, business, and governance" },
            { icon: "💻", text: "A digital-first model built for flexible and accessible higher education" },
            { icon: "🇬🇧", text: "Leadership rooted in UK and international higher education experience" },
            { icon: "🌍", text: "Strong external networks across education, youth leadership, and global engagement" },
            { icon: "📋", text: "An emerging governance and quality assurance framework designed to support disciplined growth" },
          ].map((s, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 16,
                alignItems: "flex-start",
                background: "#fff",
                borderRadius: 10,
                padding: "24px 20px",
                border: "1px solid rgba(12,31,63,0.06)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                transitionDelay: `${i * 0.1}s`,
                cursor: "default",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-3px)";
                e.currentTarget.style.boxShadow = "0 8px 28px rgba(12,31,63,0.07)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.03)";
              }}
            >
              <span style={{ fontSize: 28, flexShrink: 0, lineHeight: 1 }}>{s.icon}</span>
              <p style={{ fontSize: 14, lineHeight: 1.65, color: "#2D2D44", margin: 0 }}>{s.text}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="sa-cta-section">
        <div className="sa-section-label" style={{ color: "#C5A44E" }}>Get Involved</div>
        <h2 className="sa-cta-title">Join Our Journey</h2>
        <p className="sa-cta-desc">
          We are building a higher education institution that is digitally
          enabled, globally minded, and publicly relevant. We invite learners,
          partners, and stakeholders to engage with us.
        </p>
        <div className="sa-cta-links">
          <button className="sa-cta-btn primary" onClick={() => window.open("https://eusbd.eu", "_blank")}>
            Visit eusbd.eu
          </button>
          <button className="sa-cta-btn secondary" onClick={() => { window.location.href = "mailto:eusbdteam@eusbd.eu"; }}>
            Contact Us
          </button>
        </div>
      </section>
    </div>
  );
};

export default StrategicDesign2Clean;

